import { storageService } from '../infrastructure/storage/storageService';

/**
 * lookupCacheService — Stale-While-Revalidate cache for all lookup data.
 *
 * Strategy:
 *  1. On request: return cached data from storageService immediately (zero wait).
 *  2. If cache is older than TTL (24h): silently fetch fresh data in the background.
 *  3. Next render cycle picks up the updated cache automatically.
 *
 * This pattern ensures:
 *  - Zero loading state for lookups after first visit.
 *  - Data is always at most 24 hours old.
 *  - No blocking on the critical rendering path.
 *
 * Storage: platform-agnostic storageService (Web localStorage / Mobile AsyncStorage).
 * TTL: 24 hours by default (matches server-side Caffeine TTL).
 */

const CACHE_PREFIX = 'xsvc_lookup_';
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
  version: number; // bump to force invalidation on schema changes
}

const CACHE_VERSION = 1;
const trackedLookupKeys = new Set<string>();

// ── Core cache operations ─────────────────────────────────────────────────────

function readCache<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = storageService.get(`${CACHE_PREFIX}${key}`);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    // Invalidate on version mismatch
    if (entry.version !== CACHE_VERSION) return null;
    trackedLookupKeys.add(key);
    return entry;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      fetchedAt: Date.now(),
      version: CACHE_VERSION,
    };
    storageService.set(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
    trackedLookupKeys.add(key);
  } catch {
    // storage full or unavailable — fail silently, data still served from memory
  }
}

function isStale(entry: CacheEntry<unknown>, ttlMs = DEFAULT_TTL_MS): boolean {
  return Date.now() - entry.fetchedAt > ttlMs;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Gets lookup data with stale-while-revalidate semantics.
 *
 * - If cache hit (not stale): returns immediately, no network call.
 * - If cache hit (stale):     returns stale data immediately + fires background refresh.
 * - If cache miss:            awaits the fetcher, stores result, returns data.
 *
 * @param key     - Unique cache key (e.g. 'genders', 'bloodGroups')
 * @param fetcher - Async function that fetches fresh data from the API
 * @param ttlMs   - Cache TTL in ms (default: 24h)
 */
export async function getLookup<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs = DEFAULT_TTL_MS
): Promise<T> {
  const cached = readCache<T>(key);

  if (cached !== null) {
    if (isStale(cached, ttlMs)) {
      // Stale — serve cached immediately, refresh in background
      fetcher()
        .then(fresh => writeCache(key, fresh))
        .catch(() => {}); // Background refresh failure is silent
    }
    return cached.data;
  }

  // Cache miss — must fetch synchronously
  const fresh = await fetcher();
  writeCache(key, fresh);
  return fresh;
}

/**
 * Forces an immediate refresh of a specific lookup key.
 * Useful after admin edits to reference data.
 */
export async function invalidateLookup<T>(
  key: string,
  fetcher: () => Promise<T>
): Promise<T> {
  storageService.remove(`${CACHE_PREFIX}${key}`);
  trackedLookupKeys.delete(key);
  const fresh = await fetcher();
  writeCache(key, fresh);
  return fresh;
}

/**
 * Clears all lookup caches from storage.
 * Call on logout to prevent stale data for the next user.
 */
export function clearAllLookupCaches(): void {
  trackedLookupKeys.forEach(k => {
    storageService.remove(`${CACHE_PREFIX}${k}`);
  });
  trackedLookupKeys.clear();

  // Also clean any browser localStorage remnants if available
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k?.startsWith(CACHE_PREFIX)) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => window.localStorage.removeItem(k));
    } catch {
      // ignore
    }
  }
}

